/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { shopAPI } from 'api/shop'
import { RootState } from 'store/store'
import { BillingCycleName, OrderType, ProductServiceType } from 'models/enums'
import { BaseHosting, CartItem, ConfigOptionGroup, PendingItem } from 'models/shop'

/**********************************************************************************************************
 *    SLICE
 **********************************************************************************************************/
interface ShopState {
    productGroup: null,
    searchTerm: undefined,
    customFieldsOpen: boolean,
    cart: null | {
        account_credit: number,
        account_id: null | number,
        create_at: null | string,
        items: CartItem[],
        pricing: {
            item_discount: number,
            order_discount: number,
            subtotal: number,
            total: number,
            total_discount: number,
            total_due: number
        },
        user_id: number,
        uuid: null | string,
        promo_code: null | string
    },
    cartStatus: 'idle' | 'loading' | 'success' | 'error',
    cartUpdateStatus: 'idle' | 'loading' | 'success' | 'error',
    cartOpen: false,
    cartItem: {
        template: 'cart' | 'clear' | 'contact' | 'authentication status' | 'signup',
        item: CartItem
    },
    order: null | {
        id?: undefined | number,
        invoice: {
            id: null | number
        }
    },
    paymentMethod: null | {
        id: number
        identifier: string
    },
    checkoutStep: 'payment' | 'checkout',
    items: [] | PendingItem[],
    domain: null | string,
    selectedDomain: null,
    product: null | string,
    productType: string,
    hosting: {
        config_option_groups: ConfigOptionGroup[]
    },
    defaultBillingCycle: string,
    baseConfigOptions: { name: string, value: string }[],
    baseHosting: BaseHosting,
    total: number
}

const initialState: ShopState = {
    productGroup: null,
    searchTerm: undefined,
    customFieldsOpen: false,
    cart: {
        account_credit: 0,
        account_id: null,
        create_at: null,
        items: [],
        pricing: {
            item_discount: 0,
            order_discount: 0,
            subtotal: 0,
            total: 0,
            total_discount: 0,
            total_due: 0
        },
        user_id: 0,
        uuid: null,
        promo_code: null
    },
    cartStatus: 'idle',
    cartUpdateStatus: 'idle',
    cartOpen: false,
    cartItem: {
        template: 'cart',
        item: {
            id: null,
            auto_renew: false,
            billing_cycle_id: 0,
            is_premium: false,
            item_uuid: null,
            name: '',
            order_type: OrderType.All,
            price: {
                discount: '0.00',
                product_price: '0.00',
                subtotal: '0.00',
                total: '0.00',
                total_config_price: '0.00'
            },
            product_id: 0,
            quantity: 0,
            domain_contact_fields: null
        }
    },
    order: null,
    paymentMethod: null,
    checkoutStep: 'payment',
    items: [],
    domain: null,
    selectedDomain: null,
    product: null,
    productType: '',
    hosting: {
        config_option_groups: [{
            id: 0,
            name: '',
            config_options: [{
                id: 0,
                name: '',
                description: '',
                input_type: '',
                values: [{
                    display_order: 0,
                    id: 0,
                    name: '',
                    pricing: [{
                        billing_cycle: {
                            id: 0,
                            name: BillingCycleName.Monthly,
                            interval: ''
                        },
                        currency: '',
                        price: 0,
                        type: '',
                        billing_cycle_id: 0
                    }],
                    show_order: 0
                }]
            }]
        }]
    },
    defaultBillingCycle: 'Monthly',
    baseConfigOptions: [
        { name: 'Memory', value: '2GB' },
        { name: 'CPU', value: '200%' },
        { name: 'Disk Space', value: '5GB' }
    ],
    baseHosting: {
        billing_cycle_id: 0,
        addons: [],
        product_id: 0,
        product_type: ProductServiceType.SharedHosting,
        order_type: OrderType.All,
        config_options: undefined,
        pricing_list: [],
        pricing: 0
    },
    total: 0
}

export const shopSlice = createSlice({
    name: 'shop',
    initialState,
    reducers: {
        setProductType: (state, { payload }) => {
            // @ts-ignore
            state.productType = payload
        },
        setSearchTerm: (state, { payload }) => {
            state.searchTerm = payload
        },
        setCustomFieldsOpen: (state, { payload }) => {
            state.customFieldsOpen = payload
        },
        addToItems: (state, { payload }) => {
            const pricingInCent = parseFloat(payload.pricing) * 100
            state.items = [...state.items, { ...payload, pricing: pricingInCent }]
            state.total = state.total + pricingInCent
            state.product = payload.product_id

            if (payload.eligibility_fields && payload.eligibility_fields.length >= 1) {
                state.customFieldsOpen = true
            }

            if (payload.product_type === 'domain') {
                state.domain = payload.product_id
            }

            if (state.productType === 'web-hosting') {
                state.items = state.items.map((item) => {
                    if (item.order_type === 'all') {
                        return {
                            ...item,
                            name: payload.name
                        }
                    }

                    return item
                })
            }
        },
        assignHostingName: (state, { payload }) => {
            state.items = state.items.map((item) => {
                if (item.product_id === state.product) {
                    return {
                        ...item,
                        name: payload.name
                    }
                }

                return item
            })
        },
        resetHostingName: (state) => {
            state.items = state.items.map((item) => {
                if (item.product_id === state.product) {
                    return {
                        ...item,
                        name: undefined
                    }
                }

                return item
            })
        },
        removeFromItemsByNameID: (state, { payload }) => {
            const { pricing }: any = state.items.find(({ name, product_id }) => {
                return name === payload.name && product_id === payload.product_id
            })
            state.total = state.total - pricing
            state.items = state.items.filter(({ name, product_id }) => {
                return !(name === payload.name && product_id === payload.product_id)
            })

            if (state.productType === 'web-hosting' && payload.pathname !== '/checkout') {
                state.items = state.items.map((item) => {
                    if (item.name === payload.name) {
                        return {
                            ...item,
                            name: undefined
                        }
                    }

                    return item
                })
            }
        },
        removeFromItemsByID: (state, { payload }) => {
            const { pricing }: any = state.items.find(({ id }) => id === payload.id)
            state.total = state.total - pricing
            state.items = state.items.filter(({ id }) => id !== payload.id)
        },
        updateConfigOptions: (state, { payload: { product_id, config_id, option_id } }) => {
            const timestamp = Date.now()
            const { config_option_groups } = state.hosting
            const { config_options } = config_option_groups[0]
            state.items = state.items.map((item) => {
                if (item.product_id === product_id && item.config_options) {
                    const updatedConfig = item.config_options.map((config_option) => {
                        if (config_option.config_id === config_id) {
                            return {
                                config_id,
                                option_id
                            }
                        }

                        return {
                            config_id: config_option.config_id,
                            option_id: config_option.option_id
                        }
                    })
                    const billingCyclePricing = config_options.map((config) => {
                        const configs = updatedConfig.map(({ config_id }) => config_id)
                        const options = updatedConfig.map(({ option_id }) => option_id)

                        if (configs.includes(config.id) && config.values) {
                            const { values } = config
                            const value = values.find(({ id }) => options.includes(id))

                            if (value) {
                                const pricing = value.pricing.find(({ billing_cycle: { id } }) => id === item.billing_cycle_id)

                                return pricing ? pricing.price : 0
                            }

                            return 0
                        }

                        return 0
                    }).reduce((prev, next) => prev + next, 0)

                    return {
                        ...item,
                        timestamp,
                        config_options: updatedConfig,
                        pricing: billingCyclePricing * 100
                    }
                }

                return {
                    ...item,
                    timestamp
                }
            })
            state.total = state.items.reduce((prev, next) => prev + next.pricing, 0)
        },
        updateBillingCycle: (state, { payload: { product_id, name, billing_cycle_id, type } }) => {
            const timestamp = Date.now()

            if (type === 'custom-hosting') {
                const { config_option_groups } = state.hosting
                const { config_options } = config_option_groups[0]
                const item = state.items.find((item) => item.product_id === product_id)
                const configs = item?.config_options?.map(({ config_id }) => config_id)
                const options = item?.config_options?.map(({ option_id }) => option_id)
                const billingCyclePricing = config_options.map((config) => {
                    if (configs && configs.includes(config.id) && config.values && options) {
                        const { values } = config
                        const value = values.find(({ id }) => options.includes(id))

                        if (value) {
                            const pricing = value.pricing.find(({ billing_cycle: { id } }) => id === parseInt(billing_cycle_id))

                            return pricing ? pricing.price : 0
                        }
                    }

                    return 0
                }).reduce((prev, next) => prev + next, 0)
                state.items = state.items.map((item) => {
                    if (item.product_id === product_id) {
                        return {
                            ...item,
                            timestamp,
                            billing_cycle_id: parseInt(billing_cycle_id),
                            pricing: billingCyclePricing * 100,
                            addons: item.addons.length === 0
                                ? []
                                : item.addons.map((addon) => {
                                    const pricing = addon.pricing_list.find(({ billing_cycle: { id } }) => id === parseInt(billing_cycle_id))

                                    if (pricing) {
                                        return {
                                            ...addon,
                                            price: pricing.price * 100,
                                            billing_cycle_id: parseInt(billing_cycle_id)
                                        }
                                    }

                                    return addon
                                })
                        }
                    }

                    return item
                })

                const addonsTotal = state.items.flatMap((item) => {
                    if (item.product_id === product_id && item.name === name) {
                        if (item.addons.length >= 1) {
                            return item.addons.map(({ price }) => price)
                        }
                    }

                    return 0
                }).filter((price) => price > 0).reduce((prev, next) => prev + next, 0)

                state.total = state.items.reduce((prev, next) => prev + next.pricing, 0) + addonsTotal
            } else {
                const item = state.items.find((item) => item.product_id === product_id && item.name === name)
                const filterPricing = item?.pricing_list.filter((price) => price.type === type)
                const pricing = filterPricing?.find(({ billing_cycle_id: id }) => id === billing_cycle_id)

                state.items = state.items.map((item) => {
                    if (item.product_id === product_id && item.name === name && pricing) {
                        return {
                            ...item,
                            timestamp,
                            billing_cycle_id,
                            pricing: pricing.price * 100
                        }
                    }

                    return item
                })

                state.total = state.items.reduce((prev, next) => prev + next.pricing, 0)
            }
        },
        addAddonToItem: (state, { payload: { product_id, id, price, name, pricing_list } }) => {
            state.items = state.items.map((item) => {
                if (item.product_id === product_id && item.billing_cycle_id) {
                    return {
                        ...item,
                        addons: [
                            ...item.addons,
                            {
                                name,
                                pricing_list,
                                product_id: id,
                                billing_cycle_id: item.billing_cycle_id,
                                price: price * 100
                            }
                        ]
                    }
                }

                return item
            })
            state.total = state.total + price * 100
        },
        removeAddonFromItem: (state, { payload: { product_id, id, price } }) => {
            const timestamp = Date.now()
            state.items = state.items.map((item) => {
                if (item.product_id === product_id) {
                    return {
                        ...item,
                        timestamp,
                        addons: item.addons.filter((addon) => addon.product_id !== id)
                    }
                }

                return item
            })
            state.total = state.total - price * 100
        },
        toggleAutoRenew: (state, { payload: { product_id, name, auto_renew } }) => {
            const timestamp = Date.now()
            state.items = state.items.map((item) => {
                if (item.name === name && item.product_id === product_id) {
                    return {
                        ...item,
                        timestamp,
                        auto_renew: !auto_renew
                    }
                }

                return item
            })
        },
        toggleIDProtection: (state, { payload: { product_id, name, id_protection } }) => {
            const timestamp = Date.now()
            state.items = state.items.map((item) => {
                if (item.name === name && item.product_id === product_id) {
                    return {
                        ...item,
                        timestamp,
                        id_protection: !id_protection
                    }
                }

                return item
            })
        },
        addCustomField: (state, { payload: { product_id, custom_fields } }) => {
            state.items = state.items.map((item) => {
                if (item.product_id === product_id) {
                    return {
                        ...item,
                        custom_fields
                    }
                }

                return item
            })
        },
        addEPPCode: (state, { payload }) => {
            state.items = state.items.map((item) => {
                if (item.product_id === payload.product_id && item.name === payload.name) {
                    return {
                        ...item,
                        epp: payload.epp
                    }
                }

                return item
            })
        },
        addContactToDomain: (state, { payload }) => {
            state.items = state.items.map((item) => {
                if (item.product_id === payload.product_id && item.name === payload.name) {
                    return {
                        ...item,
                        domain_contact_fields: payload.contact
                    }
                }

                return item
            })
        },
        resetProduct: (state) => {
            state.product = null
        },
        clearItems: (state) => {
            state.items = []
            state.total = 0
        },
        setCartOpen: (state, { payload }) => {
            state.cartOpen = payload
        },
        setCartItem: (state, { payload }) => {
            state.cartItem = payload
        },
        setCartUpdateStatus: (state, { payload }) => {
            state.cartUpdateStatus = payload
        },
        setPaymentMethod: (state, { payload }) => {
            state.paymentMethod = payload
        },
        setCheckoutStep: (state, { payload }) => {
            state.checkoutStep = payload
        }
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                shopAPI.endpoints.createCart.matchPending,
                (state, { payload }) => {
                    state.cartStatus = 'loading'
                }
            )
            .addMatcher(
                shopAPI.endpoints.createCart.matchFulfilled,
                (state, { payload }) => {
                    state.cartStatus = 'success'
                    state.cart = payload
                    const { timestamp } = payload
                    state.items = state.items.map((pendingItem) => {
                        const itemAddedToCart = ({ name, product_id }: { name: string, product_id: number }) => {
                            return name === pendingItem.name && product_id === pendingItem.product_id
                        }

                        if (payload.items.some(itemAddedToCart)) {
                            const cartItem = payload.items.find(itemAddedToCart)

                            if (pendingItem.addons && cartItem) {
                                return {
                                    ...pendingItem,
                                    item_uuid: cartItem.item_uuid,
                                    timestamp,
                                    addons: pendingItem.addons.map((pendingAddon) => {
                                        if (cartItem.addons) {
                                            const cartItemAddon = cartItem.addons.find((addon) => addon.product_id === pendingAddon.product_id)

                                            return {
                                                ...pendingAddon,
                                                item_uuid: cartItemAddon?.item_uuid
                                            }
                                        }

                                        return pendingAddon
                                    })
                                }
                            }

                            return {
                                ...pendingItem,
                                item_uuid: cartItem?.item_uuid,
                                timestamp
                            }
                        }

                        return pendingItem
                    })
                }
            )
            .addMatcher(
                shopAPI.endpoints.createCart.matchRejected,
                (state, { payload }) => {
                    state.cartStatus = 'error'
                    state.cart = null
                    state.items = []
                    state.total = 0
                }
            )
            .addMatcher(
                shopAPI.endpoints.updateCartItems.matchPending,
                (state) => {
                    state.cartUpdateStatus = 'loading'
                }
            )
            .addMatcher(
                shopAPI.endpoints.updateCartItems.matchFulfilled,
                (state, { payload }) => {
                    state.cartUpdateStatus = 'success'
                    state.cart = Array.isArray(payload.cart) ? null : payload.cart
                    // @ts-ignore
                    state.items = state.items.map((item) => {
                        if (item.product_id === state.cartItem.item.product_id && item.name === state.cartItem.item.name) {
                            return {
                                ...state.cartItem.item,
                                pricing_list: item.pricing_list,
                                product_type: item.product_type
                            }
                        }

                        return item
                    })
                    if (payload.remove_items) {
                        state.items = state.items.map((item) => {
                            if (item.addons) {
                                return {
                                    ...item,
                                    addons: item.addons.filter((addon) => !payload.remove_items.includes(addon.item_uuid))
                                }
                            }

                            return item
                        }).filter(({ item_uuid }) => !payload.remove_items.includes(item_uuid))
                        state.total = state.items.filter(({ item_uuid }) => !payload.remove_items.includes(item_uuid)).reduce((prev, next) => {
                            if (next.addons) {
                                const addonPrice = next.addons.filter((addon) => !payload.remove_items.includes(addon.item_uuid)).reduce((addonPrev, addonNext) => addonPrev + addonNext.price, 0)

                                return prev + next.pricing + addonPrice
                            }

                            return prev + next.pricing
                        }, 0)
                    }
                }
            )
            .addMatcher(
                shopAPI.endpoints.product.matchFulfilled,
                (state, { payload }) => {
                    if (payload.service_type === 'shared_hosting') {
                        const { id, pricing: productPricing, config_option_groups } = payload
                        const baseConfig = state.baseConfigOptions.map(({ name }) => name)
                        const { id: billing_cycle_id }: any = productPricing.map(({ billing_cycle }) => billing_cycle).find(({ name }) => name === state.defaultBillingCycle)
                        const { config_options } = config_option_groups[0]

                        const config = config_options.map((config) => {
                            if (baseConfig.includes(config.name) && config.values) {
                                const baseConfigOption = state.baseConfigOptions.find(({ name }) => name === config.name)
                                const option = config.values.find(({ name }) => name === baseConfigOption?.value)

                                return {
                                    config_id: config.id,
                                    option_id: option?.id
                                }
                            }

                            return {}
                        })

                        const pricingBillingCycle = config_options.map((config) => {
                            if (baseConfig.includes(config.name) && config.values) {
                                const baseConfigOption = state.baseConfigOptions.find(({ name }) => name === config.name)
                                const { values } = config
                                const value = values.find(({ name }: { name: string }) => name === baseConfigOption?.value)
                                const pricing = value?.pricing.find(({ billing_cycle: { id } }) => id === billing_cycle_id)

                                return pricing ? pricing.price : 0
                            }

                            return 0
                        }).reduce((prev: number, next: number) => prev + next, 0)

                        state.hosting = payload
                        state.baseHosting = {
                            billing_cycle_id,
                            addons: [],
                            product_id: id,
                            product_type: ProductServiceType.SharedHosting,
                            order_type: OrderType.All,
                            config_options: config,
                            pricing_list: productPricing,
                            pricing: pricingBillingCycle
                        }
                    }
                }
            )
            .addMatcher(
                shopAPI.endpoints.checkoutCart.matchFulfilled,
                (state, { payload }) => {
                    state.order = payload
                }
            )
            .addMatcher(
                shopAPI.endpoints.viewCart.matchFulfilled,
                (state, { payload }) => {
                    state.cart = payload
                }
            )
            .addMatcher(
                shopAPI.endpoints.deleteCart.matchFulfilled,
                (state, { payload }) => {
                    state.cart = null
                    state.items = []
                    state.total = 0
                }
            )
    }
})

export const {
    setProductType,
    setSearchTerm,
    setCustomFieldsOpen,
    addToItems,
    assignHostingName,
    resetHostingName,
    removeFromItemsByNameID,
    removeFromItemsByID,
    updateConfigOptions,
    updateBillingCycle,
    addAddonToItem,
    removeAddonFromItem,
    toggleAutoRenew,
    toggleIDProtection,
    addCustomField,
    addEPPCode,
    addContactToDomain,
    resetProduct,
    clearItems,
    setCartOpen,
    setCartItem,
    setCartUpdateStatus,
    setPaymentMethod,
    setCheckoutStep
} = shopSlice.actions

export default shopSlice.reducer

export const getMemoizedPromoCodes = createSelector(
    (state: RootState) => state.shop.cart,
    (cart) => {
        const promoCodes = cart?.items
            ? cart.items.map((item) => {
                if (item.promo_code) {
                    return item.promo_code
                }

                return null
            })
            : null

        if (Array.isArray(promoCodes)) {
            return [cart?.promo_code, ...promoCodes].filter((promoCode) => promoCode)
        }

        if (cart?.promo_code) {
            return [cart.promo_code]
        }

        return []
    }
)
